// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import ModalPortal from "./ModalPortal";
import PrivacyNoticeLink from "./PrivacyNoticeLink";
import {
  updateUserInfo,
  fetchTwoFactorStatus,
  createTwoFactorSetup,
  enableTwoFactor,
  disableTwoFactor,
} from "../services/api";

const modalStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(0,0,0,0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1200,
};

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  padding: "4vw 2vw",
  minWidth: 220,
  maxWidth: "98vw",
  width: "100%",
  boxSizing: "border-box",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  margin: "12px 0",
  borderRadius: 6,
  border: "1px solid #ddd",
  fontSize: 16,
};

const buttonStyle = {
  marginTop: 18,
  padding: "10px 24px",
  borderRadius: 6,
  border: "none",
  background: "#3256a2",
  color: "#fff",
  fontWeight: 600,
  fontSize: 16,
  cursor: "pointer",
};

const AccountModal = ({ open, onClose }: any) => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState<any>(false);
  const [message, setMessage] = useState<any>("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState<any>(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState<any>(false);
  const [setupSecret, setSetupSecret] = useState<any>("");
  const [setupUri, setSetupUri] = useState<any>("");
  const [enableOtp, setEnableOtp] = useState<any>("");
  const [disableOtp, setDisableOtp] = useState<any>("");
  const [twoFactorMessage, setTwoFactorMessage] = useState<any>("");

  useEffect(() => {
    if (!open) return;
    setEmail(user?.email || "");
    setPhone(user?.phone || "");
    setTwoFactorMessage("");
    setEnableOtp("");
    setDisableOtp("");

    let mounted = true;
    (async () => {
      setTwoFactorLoading(true);
      try {
        const status = await fetchTwoFactorStatus();
        if (!mounted) return;
        setTwoFactorEnabled(Boolean(status?.enabled));
      } catch (err: any) {
        if (!mounted) return;
        setTwoFactorMessage(err.message || "Could not load 2FA status.");
      } finally {
        if (mounted) setTwoFactorLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, user?.email, user?.phone]);

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      await updateUserInfo({ email, phone });
      setMessage("Saved!");
      setTimeout(() => {
        setSaving(false);
        onClose();
      }, 1200);
    } catch (err: any) {
      setMessage("Error saving info.");
      setSaving(false);
    }
  };

  const handleCreateSetup = async () => {
    setTwoFactorLoading(true);
    setTwoFactorMessage("");
    try {
      const setup = await createTwoFactorSetup();
      setSetupSecret(setup?.secret || "");
      setSetupUri(setup?.otpauth_uri || "");
      setTwoFactorMessage("Scan the QR code (or enter the secret manually), then verify with a 6-digit code.");
    } catch (err: any) {
      setTwoFactorMessage(err.message || "Could not create 2FA setup.");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!setupSecret) {
      setTwoFactorMessage("Generate a setup secret first.");
      return;
    }
    if (!enableOtp || enableOtp.length !== 6) {
      setTwoFactorMessage("Enter a valid 6-digit code from your authenticator app.");
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorMessage("");
    try {
      await enableTwoFactor(setupSecret, enableOtp);
      setTwoFactorEnabled(true);
      setSetupSecret("");
      setSetupUri("");
      setEnableOtp("");
      setTwoFactorMessage("2FA enabled successfully.");
    } catch (err: any) {
      setTwoFactorMessage(err.message || "Could not enable 2FA.");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableOtp || disableOtp.length !== 6) {
      setTwoFactorMessage("Enter a valid 6-digit code to disable 2FA.");
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorMessage("");
    try {
      await disableTwoFactor(disableOtp);
      setTwoFactorEnabled(false);
      setDisableOtp("");
      setTwoFactorMessage("2FA disabled.");
    } catch (err: any) {
      setTwoFactorMessage(err.message || "Could not disable 2FA.");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const qrUrl = setupUri
    ? `https://quickchart.io/qr?size=180&text=${encodeURIComponent(setupUri)}`
    : "";

  return (
    <ModalPortal>
    <div style={modalStyle}>
      <div style={{ ...cardStyle, maxWidth: 520 }}>
        <h2 style={{ marginBottom: 18 }}>Manage Account</h2>
        <label>Email</label>
        <input style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} />
        <label>Phone</label>
        <input style={inputStyle} value={phone} onChange={e => setPhone(e.target.value)} />
        <button style={buttonStyle} onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
        <button style={{ ...buttonStyle, background: "#d32f2f", marginLeft: 12 }} onClick={onClose} disabled={saving}>Cancel</button>
        {message && <div style={{ marginTop: 16, color: message === "Saved!" ? "#388e3c" : "#d32f2f" }}>{message}</div>}
        <div style={{ marginTop: 12 }}><PrivacyNoticeLink /></div>

        <div style={{ marginTop: 22, borderTop: "1px solid #e5e7eb", paddingTop: 18 }}>
          <h3 style={{ margin: "0 0 8px", color: "#1f3b7b" }}>Two-Factor Authentication (2FA)</h3>
          <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
            Use any free authenticator app (Google Authenticator, Microsoft Authenticator, Authy).
          </div>

          <div style={{ marginBottom: 10, fontSize: 14 }}>
            Status: <strong style={{ color: twoFactorEnabled ? "#15803d" : "#b45309" }}>{twoFactorEnabled ? "Enabled" : "Disabled"}</strong>
          </div>

          {!twoFactorEnabled && (
            <div style={{ display: "grid", gap: 10 }}>
              {!setupSecret && (
                <button style={{ ...buttonStyle, marginTop: 0 }} onClick={handleCreateSetup} disabled={twoFactorLoading}>
                  {twoFactorLoading ? "Preparing..." : "Set Up 2FA"}
                </button>
              )}

              {setupSecret && (
                <>
                  {qrUrl && (
                    <img
                      src={qrUrl}
                      alt="2FA QR code"
                      style={{ width: 180, height: 180, borderRadius: 8, border: "1px solid #e5e7eb" }}
                    />
                  )}
                  <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.4 }}>
                    Secret: <code>{setupSecret}</code>
                  </div>
                  <input
                    style={inputStyle}
                    placeholder="Enter 6-digit code"
                    value={enableOtp}
                    onChange={(e) => setEnableOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                  <button style={{ ...buttonStyle, marginTop: 0 }} onClick={handleEnable2FA} disabled={twoFactorLoading}>
                    {twoFactorLoading ? "Enabling..." : "Enable 2FA"}
                  </button>
                </>
              )}
            </div>
          )}

          {twoFactorEnabled && (
            <div style={{ display: "grid", gap: 10 }}>
              <input
                style={inputStyle}
                placeholder="Enter 6-digit code to disable"
                value={disableOtp}
                onChange={(e) => setDisableOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button
                style={{ ...buttonStyle, marginTop: 0, background: "#b91c1c" }}
                onClick={handleDisable2FA}
                disabled={twoFactorLoading}
              >
                {twoFactorLoading ? "Disabling..." : "Disable 2FA"}
              </button>
            </div>
          )}

          {twoFactorMessage && (
            <div style={{ marginTop: 10, fontSize: 13, color: twoFactorMessage.toLowerCase().includes("success") || twoFactorMessage.toLowerCase().includes("enabled") || twoFactorMessage.toLowerCase().includes("disabled") ? "#166534" : "#b91c1c" }}>
              {twoFactorMessage}
            </div>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default AccountModal;
